import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Bell, FileText, Check, Palmtree, ClipboardList, UserCheck, AlertCircle, CheckCheck } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { fmtDate } from '@/lib/dateFmt';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ICON_BY_TYPE = {
  document: FileText,
  rapportino_citato: ClipboardList,
  rapportino_status: ClipboardList,
  rapportino_new: ClipboardList,
  ferie_request: Palmtree,
  ferie_approvata: Palmtree,
  ferie_rifiutata: Palmtree,
  permesso_request: UserCheck,
  permesso_approvato: UserCheck,
  permesso_rifiutato: UserCheck,
  malattia_cert: AlertCircle,
};

const DEFAULT_TITLE_BY_TYPE = {
  document: 'Nuovo Documento',
  rapportino_citato: 'Citato in un rapportino',
  rapportino_status: 'Aggiornamento rapportino',
  rapportino_new: 'Nuovo rapportino',
  ferie_request: 'Nuova richiesta ferie',
  ferie_approvata: 'Ferie approvate',
  ferie_rifiutata: 'Ferie rifiutate',
  permesso_request: 'Nuova richiesta permesso',
  permesso_approvato: 'Permesso approvato',
  permesso_rifiutato: 'Permesso rifiutato',
  malattia_cert: 'Certificato malattia',
};

export default function AdminNotifichePage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications(notifications.map(n => n.id === id ? {...n, read: true} : n));
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const markAllAsRead = async () => {
    const unread = notifications.filter(n => !n.read);
    await Promise.all(unread.map(n => axios.put(`${API}/notifications/${n.id}/read`, {}, { withCredentials: true }).catch(() => {})));
    setNotifications(notifications.map(n => ({...n, read: true})));
    toast.success('Tutte le notifiche segnate come lette');
  };

  const handleClick = (n) => {
    if (!n.read) markAsRead(n.id);
    if (n.url) navigate(n.url);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-notifiche-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Notifiche</h1>
          <p className="text-sm text-slate-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} non ${unreadCount === 1 ? 'letta' : 'lette'}` : 'Tutto letto'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={markAllAsRead} data-testid="mark-all-read-btn">
            <CheckCheck className="h-4 w-4 mr-2" />
            Segna tutte come lette
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Bell className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Nessuna notifica</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map((notification) => {
            const Icon = ICON_BY_TYPE[notification.type] || Bell;
            const title = notification.title || notification.document_title || DEFAULT_TITLE_BY_TYPE[notification.type] || 'Notifica';
            const message = notification.message || notification.document_title || '';
            return (
              <Card
                key={notification.id}
                data-testid={`admin-notification-item-${notification.id}`}
                className={`border-slate-200 cursor-pointer transition-all hover:shadow-sm ${!notification.read ? 'bg-blue-50 border-blue-200' : ''}`}
                onClick={() => handleClick(notification)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${notification.read ? 'bg-slate-100' : 'bg-blue-100'}`}>
                      <Icon className={`h-5 w-5 ${notification.read ? 'text-slate-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <h3 className="font-medium text-slate-900 truncate">{title}</h3>
                        {!notification.read && (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 shrink-0" variant="outline">
                            Nuovo
                          </Badge>
                        )}
                      </div>
                      {message && (
                        <p className="text-sm text-slate-700 break-words">{message}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-2">
                        {fmtDate(notification.created_at, 'dd MMM yyyy, HH:mm')}
                      </p>
                    </div>
                    {notification.read && (
                      <Check className="h-4 w-4 text-slate-400 shrink-0" />
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
